import { patchProfileSettings } from './cloudService';
import { supabase } from './supabaseClient';

/**
 * Vorlagen fuer die BELEGUEBERSCHRIFT — der fette Titel ueber dem Beleg
 * ("Rechnung", "Schlussrechnung", "Teilschlussrechnung", …).
 *
 * Drei Ebenen, und sie bleiben getrennt:
 *
 * 1. Der STANDARD je Belegart (invoiceLogic.defaultInvoiceTitle) — er gilt
 *    weiter, solange nichts anderes dasteht, und wird hier nicht angetastet.
 * 2. Die globale Standard-Ueberschrift (InvoiceConfig.defaultHeaderText) aus
 *    den Einstellungen — sie verdraengt den Standardtitel auf allen Belegen
 *    ohne eigene Ueberschrift.
 * 3. Die Ueberschrift DIESES Belegs (details.headerText) — sie wird beim
 *    Ausstellen mit dem Beleg eingefroren.
 *
 * Dieses Modul liefert nur die AUSWAHLLISTE fuer Ebene 3. Eine Vorlage ist
 * eine Tipphilfe und kein Zustand des Belegs: gewaehlt wird sie in den Text
 * geschrieben, danach haengt der Beleg an ihr nicht mehr. Wer eine Vorlage
 * spaeter loescht, aendert damit keinen einzigen Beleg.
 *
 * Gespeichert in profiles.settings.invoiceHeaderTemplates — eine eigene,
 * flache Liste NEBEN invoiceConfig und ausdruecklich nicht darin: der
 * Rechnungsblock gehoert dem Einstellungsdialog, der ihn vollstaendig
 * verwaltet, und mergeProfileSettings mischt flach. Schriebe der Editor ihn
 * mit, machte er die dort aufgeloesten Standardwerte zu gespeicherten Werten.
 *
 * Kein neuer Tabellenanbau: es ist eine Wortliste, sie braucht weder Rechte
 * noch Fremdschluessel. (Die Kostenstellen-Bezeichnungen liegen aus
 * historischen Gruenden in einer eigenen Tabelle — das ist kein Vorbild,
 * sondern der teurere Weg.)
 */

/** Der Schluessel in profiles.settings. Eine Stelle, damit er nicht driftet. */
export const INVOICE_HEADER_TEMPLATES_KEY = 'invoiceHeaderTemplates';

/**
 * Immer vorhandene Vorschlaege.
 *
 * Die gaengigen Belegueberschriften eines Bauabrechnungsbetriebs — sie stehen
 * ohne jede Pflege bereit, damit die Auswahl nicht mit einer leeren Liste
 * beginnt. Bewusst nur Forderungsbelege: Storno, Korrektur und Gutschrift
 * tragen ihre Belegart als Standardtitel und sollen nicht versehentlich als
 * "Rechnung" ueberschrieben werden.
 *
 * "Teilschlussrechnung" hat keine eigene Belegart — sie ist eine
 * Schlussrechnung ueber einen Bauabschnitt und genau der Fall, fuer den es die
 * freie Ueberschrift gibt.
 */
export const BUILTIN_HEADER_TEMPLATES: readonly string[] = [
    'Rechnung',
    'Abschlagsrechnung',
    'Teilschlussrechnung',
    'Schlussrechnung',
    'Restrechnung',
];

/** Ein Eintrag der Auswahlliste. */
export interface HeaderTemplateOption {
    text: string;
    /**
     * Fest eingebaut — nicht loeschbar. Ein Loeschknopf daneben verspraeche
     * etwas, das kein Speicher zuruecknehmen kann: beim naechsten Oeffnen
     * staende der Eintrag wieder da.
     */
    builtin: boolean;
}

/** Vergleichsform: Gross-/Kleinschreibung und Leerraum sind kein Unterschied. */
const key = (text: string): string => text.trim().toLowerCase();

/**
 * Eine Ueberschrift auf eine Zeile bringen.
 *
 * Die Ueberschrift ist einzeilig; ein eingebettetes \n erschiene im PDF als
 * Kaestchen (dieselbe Regel wie in quoteHeadlineText). Mehrfacher Leerraum
 * wird zusammengezogen, damit zwei optisch gleiche Vorlagen nicht als zwei
 * verschiedene nebeneinander stehen.
 */
export const normalizeHeaderTemplate = (text: string): string =>
    text.replace(/\s+/g, ' ').trim();

/**
 * Die gespeicherte Liste aus profiles.settings — gegen alles gehaertet, was in
 * einem jsonb stehen kann.
 *
 * Das Feld ist ungetypt gespeichert; ein Fremdschreiber oder ein alter Stand
 * koennte dort eine Zahl, null oder gar kein Feld hinterlassen haben. Ein
 * Fehler beim Auslesen risse den Rechnungseditor auf — wegen einer Wortliste.
 * Deshalb: alles, was kein brauchbarer Text ist, faellt still weg.
 *
 * Alphabetisch wie die Kostenstellen-Vorlagen: die Liste wird gelesen, nicht
 * durchgeblaettert.
 */
export const normalizeHeaderTemplates = (raw: unknown): string[] => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>(BUILTIN_HEADER_TEMPLATES.map(key));
    const out: string[] = [];
    for (const entry of raw) {
        if (typeof entry !== 'string') continue;
        const text = normalizeHeaderTemplate(entry);
        if (!text || seen.has(key(text))) continue;
        seen.add(key(text));
        out.push(text);
    }
    return out.sort((a, b) => a.localeCompare(b, 'de'));
};

/**
 * Die Auswahlliste: erst die eingebauten Vorschlaege in ihrer fachlichen
 * Reihenfolge (Abschlag vor Schluss vor Rest), dann die eigenen.
 *
 * Nicht alphabetisch ueber beides: die eingebauten stehen in der Reihenfolge,
 * in der die Belege eines Bauvorhabens entstehen, und diese Ordnung ist mehr
 * wert als ein durchgaengiges Alphabet.
 */
export const headerTemplateOptions = (saved?: unknown): HeaderTemplateOption[] => [
    ...BUILTIN_HEADER_TEMPLATES.map((text) => ({ text, builtin: true })),
    ...normalizeHeaderTemplates(saved).map((text) => ({ text, builtin: false })),
];

/**
 * Eine Ueberschrift in die eigenen Vorlagen aufnehmen.
 *
 * Gibt `null` zurueck, wenn nichts zu speichern ist — leerer Text, oder der
 * Eintrag steht schon da (auch als eingebauter). Der Aufrufer spart sich damit
 * den Schreibvorgang; ein "gespeichert" ohne Aenderung waere eine Zusage ohne
 * Vorgang.
 */
export const addHeaderTemplate = (saved: unknown, text: string): string[] | null => {
    const value = normalizeHeaderTemplate(text ?? '');
    if (!value) return null;
    const current = normalizeHeaderTemplates(saved);
    if (BUILTIN_HEADER_TEMPLATES.some((b) => key(b) === key(value))) return null;
    if (current.some((t) => key(t) === key(value))) return null;
    return [...current, value].sort((a, b) => a.localeCompare(b, 'de'));
};

/**
 * Eine eigene Vorlage entfernen. Eingebaute lassen sich nicht loeschen — sie
 * stehen gar nicht erst in der gespeicherten Liste.
 */
export const removeHeaderTemplate = (saved: unknown, text: string): string[] => {
    const value = key(text ?? '');
    return normalizeHeaderTemplates(saved).filter((t) => key(t) !== value);
};

/**
 * Die gespeicherte Liste frisch aus dem Profil.
 *
 * Die Prop des Editors ist ein Abzug vom Anmeldezeitpunkt; sie wird zwar ueber
 * die Realtime-Verbindung nachgezogen, aber daran darf eine eben gespeicherte
 * Vorlage nicht haengen — sonst verschwindet sie beim naechsten Oeffnen des
 * Editors wieder aus dem Menue.
 *
 * Wirft NICHT: eine Wortliste, die sich nicht laden laesst, darf den
 * Rechnungseditor nicht aufreissen. Dann bleiben eben die eingebauten
 * Vorschlaege.
 *
 * `null` heisst "nicht ermittelbar" und ist von der leeren Liste zu
 * unterscheiden — sonst folgt aus einem Ladefehler ein DATENVERLUST: der Editor
 * uebernaehme die leere Liste, der naechste Klick auf "Als Vorlage speichern"
 * baute darauf auf, und saveHeaderTemplates schriebe sie als vollstaendigen
 * Stand zurueck. Acht gespeicherte Vorlagen waeren weg, weil einmal eine
 * Abfrage fehlschlug. Bei `null` behaelt der Aufrufer, was er hat.
 */
export const fetchHeaderTemplates = async (userId: string): Promise<string[] | null> => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('settings')
            .eq('id', userId)
            .maybeSingle();
        if (error) throw error;
        const settings = (data as { settings?: Record<string, unknown> | null } | null)?.settings;
        return normalizeHeaderTemplates(settings?.[INVOICE_HEADER_TEMPLATES_KEY]);
    } catch (e) {
        console.warn('Vorlagen der Belegüberschrift nicht ermittelbar', e);
        return null;
    }
};

/**
 * Die Liste in profiles.settings schreiben.
 *
 * Ueber patchProfileSettings und nicht ueber ein update({ settings: … }):
 * profiles.settings ist EIN JSON-Objekt fuer mehrere Oberflaechen, und ein
 * Rueckschreiben aus einem im Speicher gehaltenen Stand loescht alles, was
 * seither woanders geschrieben wurde.
 *
 * Wirft — anders als das Laden. Wer auf "als Vorlage speichern" drueckt, muss
 * erfahren, dass nichts gespeichert wurde.
 */
export const saveHeaderTemplates = async (
    userId: string,
    templates: string[],
): Promise<void> => {
    await patchProfileSettings(userId, { [INVOICE_HEADER_TEMPLATES_KEY]: templates });
};
